import { updateSegmentManager } from '@nutrien/ddc-fe-feature-flags-react';
import { setContext, setUser } from '@sentry/react';
import { Auth } from 'aws-amplify';
import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { configCat, segmentManager } from '@/featureFlags/ConfigCat';
import { useMst } from '@/mobx-models/Root';
import { USER_SITE_ID } from '@/utilities/constants';
import { UNAUTHENTICATED_ERROR } from '@/utilities/sentryFunctions';

import { version } from '../../../package.json';
import { userEmailManagementContext } from './userEmailManagementContext';

export const UserEmailManagementHandler: FC = ({ children }) => {
  const [email, setEmail] = useState<string | undefined>(undefined);
  const flags = useContext(configCat.featureFlagManagement.context);
  const { user } = useMst();

  const refresh = useCallback(async () => {
    let newEmail = undefined;
    try {
      const currentSession = await Auth.currentSession();
      newEmail = currentSession?.getIdToken()?.payload?.email;
    } catch (error) {
      if (error !== UNAUTHENTICATED_ERROR) {
        // TODO: switch this to logError https://nutrien.atlassian.net/browse/MDP-8374
        console.error('Error setting up user email', error, {});
      }
    }
    setEmail(newEmail);
    return newEmail;
  }, [setEmail]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const siteId = useMemo(() => user.siteId || USER_SITE_ID || 'unknown site', [user.siteId]);

  useEffect(() => {
    if (email) {
      const site = user.getSiteName();
      const identify = async () => {
        // Note that, until this happens, the only feature flags available are app-specific;
        updateSegmentManager(segmentManager, {
          email,
          userId: email,
          mineSightBorer: 'true',
          site,
          siteId,
          version,
        });
      };
      identify().catch(reason => {
        // TODO: switch this to logError https://nutrien.atlassian.net/browse/MDP-8374
        console.error('Error identifying user for feature flags', reason, {
          email,
          siteId,
          site,
          version,
        });
      });
    }
  }, [email, siteId, user]);

  useEffect(() => {
    setContext('featureFlags', flags);
  }, [flags]);

  useEffect(() => setUser({ email }), [email]);

  return (
    <userEmailManagementContext.Provider value={{ email, refresh }}>
      {children}
    </userEmailManagementContext.Provider>
  );
};
